/**
 * Cookiebot's own surface, typed locally rather than through a global `Window` augmentation: this
 * package is consumed by two Next apps that each carry their own `types/globals.ts`, and a second
 * global declaration of the same interface is a merge conflict waiting to happen.
 */
interface CookiebotApi {
  consent: {
    marketing: boolean;
    necessary: boolean;
    preferences: boolean;
    statistics: boolean;
  };
  hasResponse: boolean;
}

function getCookiebot(): CookiebotApi | undefined {
  return (window as unknown as { Cookiebot?: CookiebotApi }).Cookiebot;
}

/**
 * Read straight from `process.env` rather than through an app's validated env object, because the
 * two apps validate their environment differently and this package has to work in both. Next
 * inlines `NEXT_PUBLIC_*` at build time, transpiled workspace packages included.
 */
function getCookiebotId(): string | undefined {
  return process.env.NEXT_PUBLIC_COOKIEBOT_ID;
}

export interface ConsentCategories {
  marketing: boolean;
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
}

export interface ConsentState {
  categories: ConsentCategories;
  /**
   * True once the CMP has an answer for this visitor, or once we have given up waiting for it.
   * Consumers that must load *something* either way (Segment) wait for this; consumers that
   * simply stay off until granted (TikTok) can ignore it and read `categories`.
   */
  isResolved: boolean;
}

/**
 * The starting point, and the answer whenever the CMP cannot be read: everything denied except
 * `necessary`. Fail-closed is deliberate — Matt's Phase 2 criterion is zero transmission of
 * visitor data before an affirmative answer, so an unreadable CMP must read as "no", never as
 * "not asked yet, go ahead".
 */
const DENIED: ConsentCategories = {
  marketing: false,
  necessary: true,
  preferences: false,
  statistics: false,
};

/**
 * How long we wait for Cookiebot before treating the answer as denied. Mirrors the 2s safety net
 * in the Webflow head: if uc.js is blocked, errors, or the visitor is offline, we resolve denied
 * rather than leaving Segment (and with it first-party capture) unloaded forever. Losing the gate
 * is bad; losing the first-party carve-out is worse, so the fallback resolves rather than hangs.
 *
 * The window is re-anchored by `extendConsentFallbackWindow` rather than being counted from the
 * first subscriber: subscribers exist before hydration (Sentry's instrumentation file), while
 * uc.js is only mounted `afterInteractive`, so a fixed deadline could expire while the script it
 * is waiting for was still in flight — resolving a visitor as denied whose stored answer was a
 * yes. Every re-anchor is a fresh 2s, so the deadline always measures time spent waiting on
 * Cookiebot, never time spent waiting for Cookiebot to start.
 */
const RESOLUTION_FALLBACK_MS = 2000;

const UNRESOLVED_STATE: ConsentState = { categories: DENIED, isResolved: false };

/**
 * Snapshot returned during SSR and on the very first client render. Kept identical to what the
 * server renders so `useSyncExternalStore` does not report a hydration mismatch — the watcher
 * only starts on the client, from the first subscriber or from the CMP tag's own effect,
 * whichever the app reaches first.
 */
let currentState: ConsentState = UNRESOLVED_STATE;

const listeners = new Set<(state: ConsentState) => void>();

let isWatching = false;

let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

function scheduleFallback(): void {
  if (typeof window === 'undefined' || currentState.isResolved) {
    return;
  }

  clearTimeout(fallbackTimer);
  fallbackTimer = setTimeout(resolveAsDenied, RESOLUTION_FALLBACK_MS);
}

function readCookiebotState(): ConsentState | undefined {
  const cookiebot = getCookiebot();

  if (!cookiebot?.hasResponse) {
    return;
  }

  const { marketing, preferences, statistics } = cookiebot.consent;

  return {
    categories: {
      marketing: marketing === true,
      necessary: true,
      preferences: preferences === true,
      statistics: statistics === true,
    },
    isResolved: true,
  };
}

function isSameState(a: ConsentState, b: ConsentState): boolean {
  return (
    a.isResolved === b.isResolved &&
    a.categories.marketing === b.categories.marketing &&
    a.categories.preferences === b.categories.preferences &&
    a.categories.statistics === b.categories.statistics
  );
}

function publish(next: ConsentState): void {
  if (isSameState(currentState, next)) {
    return;
  }

  currentState = next;

  for (const listener of listeners) {
    listener(currentState);
  }
}

function syncFromCookiebot(): void {
  const state = readCookiebotState();

  if (state) {
    publish(state);
  }
}

function resolveAsDenied(): void {
  clearTimeout(fallbackTimer);
  publish({ categories: currentState.categories, isResolved: true });
}

function startWatching(): void {
  if (isWatching || typeof window === 'undefined') {
    return;
  }

  isWatching = true;

  // No CBID means no banner, which means this visitor has no way to say yes. Resolve denied
  // immediately instead of waiting out the fallback: vendors stay off, Segment loads blocked,
  // and first-party capture keeps working. This is also what a production deploy that forgot
  // NEXT_PUBLIC_COOKIEBOT_ID gets — deliberately the safe failure, not the silent one.
  if (!getCookiebotId()) {
    publish({ categories: DENIED, isResolved: true });
    return;
  }

  // `CookiebotOnConsentReady` covers the returning visitor whose stored answer is applied without
  // the dialog ever rendering; the other two cover the click. Accept/decline also fire on every
  // later pageview that already has an answer, which is harmless — `publish` drops no-op updates.
  window.addEventListener('CookiebotOnConsentReady', syncFromCookiebot);
  window.addEventListener('CookiebotOnAccept', syncFromCookiebot);
  window.addEventListener('CookiebotOnDecline', syncFromCookiebot);

  // Anchored here as the backstop for a CMP tag that never mounts at all; `CookiebotScript`
  // re-anchors it as uc.js makes progress — including by calling straight into here, when this tag
  // is what starts the watcher in the first place.
  scheduleFallback();

  // uc.js may have finished before React mounted — read the current answer rather than waiting
  // for an event that already fired.
  syncFromCookiebot();
}

/** Subscribe shape `useSyncExternalStore` expects. Starts the CMP watcher on first subscriber. */
export function subscribeToConsent(onStoreChange: () => void): () => void {
  const listener = () => onStoreChange();

  listeners.add(listener);
  startWatching();

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Same subscription for callers outside React (Sentry's instrumentation file, for one). Fires
 * immediately with the current state so the caller does not need a separate initial read.
 */
export function subscribeToConsentState(listener: (state: ConsentState) => void): () => void {
  listeners.add(listener);
  startWatching();
  listener(currentState);

  return () => {
    listeners.delete(listener);
  };
}

export function getConsentSnapshot(): ConsentState {
  return currentState;
}

export function getServerConsentSnapshot(): ConsentState {
  return UNRESOLVED_STATE;
}

/**
 * The binary gate for Segment's device-mode destinations, matching the Webflow head. Per-destination
 * gating would need the exact integration names from the Segment panel, where a wrong name fails
 * silently — so this stays coarse on purpose until those destinations are categorized in the panel.
 */
export function allowsVendorDestinations(categories: ConsentCategories): boolean {
  return categories.statistics || categories.marketing;
}

/**
 * Called when the CMP script itself fails to load — blocked by an ad blocker or extension list,
 * offline, or erroring. Resolves the state as denied straight away instead of making everything
 * that waits on an answer sit through the full fallback window for one it will never get.
 */
export function markConsentUnavailable(): void {
  resolveAsDenied();
}

/**
 * Called by the CMP script tag as uc.js makes progress — once when the tag mounts and the download
 * starts, and again when the script has executed and Cookiebot itself takes over. Each call gives
 * the CMP a fresh `RESOLUTION_FALLBACK_MS` to produce an answer, so a slow uc.js delays the answer
 * instead of being overtaken by a deadline that started before it did. Ignored once the answer is
 * in — nothing can reopen a resolved state.
 *
 * It starts the watcher rather than requiring one, because the tag mounting *is* the moment waiting
 * on Cookiebot begins, and the two apps reach that moment differently. The platform app always has
 * a subscriber first (Sentry's instrumentation file, pre-hydration), but the portfolio has none —
 * there the CMP tag is the first thing in the tree, so its effect runs before `SegmentScript` and
 * `ConsentUpgradeReload` subscribe. Guarding on `isWatching` would silently drop that call and
 * leave the window anchored to whichever component happened to mount first.
 */
export function extendConsentFallbackWindow(): void {
  startWatching();
  scheduleFallback();
}

/** Test-only: drops the watcher and the cached snapshot so each test starts from a clean CMP. */
export function resetConsentStateForTests(): void {
  if (typeof window !== 'undefined') {
    window.removeEventListener('CookiebotOnConsentReady', syncFromCookiebot);
    window.removeEventListener('CookiebotOnAccept', syncFromCookiebot);
    window.removeEventListener('CookiebotOnDecline', syncFromCookiebot);
  }

  clearTimeout(fallbackTimer);
  fallbackTimer = undefined;
  listeners.clear();
  currentState = UNRESOLVED_STATE;
  isWatching = false;
}
